import React, { useEffect, useState } from "react";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import { FormContext, useForm } from "react-hook-form";
import { Card } from "reactstrap";
import CustomCardTitle from "../../../../interfaces/common/card/CustomCardTitle";
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
} from "@material-ui/core";
import ProgressSaveButton from "../../../../components/_Layout/Buttons/ProgressSaveButton";
import { IEnrollmentSearchResult } from "../../../../interfaces/student/enrollment/EnrollmentSearchResult";
import CreatePaymentPlan from "./createPaymentPlan";
import PaymentPlanAutoComplete from "../../../../components/AutoComplete/PaymentPlanAutoComplete";
import * as PaymentPlanApi from '../../../../api/student/financials/paymentPlan/paymentPlanApi';
import StudentPaymentPlanTable from "./StudentPaymentPlanTable";
import CustomSnackbar from "../../../../components/notifications/CustomSnackbar";
import { SnackBarStateProps } from "../../profile/studentInfo/StudentInfo";
import { number } from "prop-types";
import { set } from "lodash";
import { EmptyGuid } from "../../../../utils/constants";
import { IStudentPaymentPlanSchedule } from "../../../../interfaces/student/financials/IStudentPaymentPlan";
import { format, parseISO } from "date-fns";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      minHeight: "100%",
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(1),
      overflowX: "hidden",
      overflowY: "auto",
      backgroundColor: theme.palette.background.paper,
      flexDirection: "column",
      borderRadius: theme.shape.borderRadius,
    },
    card: {
      margin: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      border: "1px solid #ccc",
      paddingBottom: theme.spacing(2),
    },
    tabContent: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(3),
    },
    paymentPlanSection: {
      marginTop: theme.spacing(3),
      marginLeft: theme.spacing(3),
    },
    updateButton: {
      "&.Mui-disabled": {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.error.contrastText,
      },
      minWidth: 100,
      height: 36
    },
    deleteButton: {
      backgroundColor: theme.palette.error.dark,
      "&:hover": {
        backgroundColor: theme.palette.error.main,
      },
      "&.Mui-disabled": {
        backgroundColor: theme.palette.error.dark,
        color: theme.palette.error.contrastText,
      },
      minWidth: 100,
      height: 36
    },
    closeOutButton: {
      "&.Mui-disabled": {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.error.contrastText,
      }
    },
  })
);

const PaymentPlans = (props: any) => {
  const classes = useStyles({});

  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState<any>(null);
  const [paymentPlanCount, setPaymentPlanCount] = useState<number>(0);

  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [confirmCloseOutOpen, setConfirmCloseOutOpen] = useState(false);

  const userSelectedCampus = useSelector((state: any) =>
    state.userstate.getSelectedCampus(state.session.user.userId)
  );

  const [snackBarProps, setSnackBarProps] = React.useState<SnackBarStateProps>({
    showSnackBar: false,
    messageInfo: undefined,
    variant: "info",
  });

  const selectedEnrollment = useSelector(
    (state: any) => state.student.selectedEnrollment
  );

  let stuEnrollId = "";
  if (selectedEnrollment) {
    stuEnrollId = selectedEnrollment.stuEnrollId;
  }

  const data = useForm<any>({ mode: "onBlur" });

  const initialModel: any = {
    stuEnrollmentId: stuEnrollId,
    numberOfPaymentPlans: 0,
    paymentPlanDescription: "",
    totalDue: 0,
    paymentPlanBalance: 0,
    planSchedule: [],
  };

  const [model, setModel] = React.useState<any>(initialModel);

  const handleFieldOnChange = (
    fieldId: string,
    value: any,
    mapFunction?: Function
  ) => {
    let updatedModel = model;
    let newValue = mapFunction ? mapFunction(value) : value;
    (updatedModel as any)[fieldId] = newValue;
    setModel({ ...updatedModel });
  };

  const getPaymentPlanByPaymentPlanId = (paymentPlanId: string) => {
    PaymentPlanApi.getStudentPaymentPlanByPPID(paymentPlanId).then((res: any) => {
      if (res && res.data) {
        const newModel = {
          stuEnrollmentId: selectedEnrollment?.stuEnrollId,
          numberOfPaymentPlans: paymentPlanCount,
          paymentPlanDescription: res.data.paymentPlanDescription || "",
          totalDue:
            Array.isArray(res.data.planSchedule)
              ? res.data.planSchedule.reduce((sum: number, a: any) => sum + (a.amount || 0), 0)
              : 0,
          paymentPlanBalance:
            Array.isArray(res.data.planSchedule)
              ? res.data.planSchedule.reduce((sum: number, a: any) => sum + (a.balance || 0), 0)
              : 0,
          paymentPlanId: res.data.paymentPlanId,
          planSchedule: res.data.planSchedule || [],
        };
        setModel(newModel);
        setSelectedPaymentPlan(res.data);
      }
    });
  };


  const refreshPaymentPlans = () => {
    if (selectedEnrollment && selectedEnrollment.stuEnrollId) {
      setSelectedPaymentPlan(null);
      setModel(initialModel);
      PaymentPlanApi.getStudentPaymentPlansByEnrollmentId(selectedEnrollment.stuEnrollId).then((res: any) => {
        if (res && res.data && res.data.length > 0) {
          const firstPlan = res.data[0];
          setPaymentPlanCount(res.data.length);
          getPaymentPlanByPaymentPlanId(firstPlan.value);

        } else {
          setPaymentPlanCount(0);
          setModel(initialModel);
        }
      });
    }
  };


  useEffect(() => {
    refreshPaymentPlans();
  }, [selectedEnrollment && selectedEnrollment.stuEnrollId]);


  const toDateOnly = (d?: string | Date | null): string | null => {
    if (!d) return null;
    const dateObj = d instanceof Date ? d : parseISO(d);
    return format(dateObj, 'yyyy-MM-dd');
  };

  const handleSave = () => {
    if (model && model.planSchedule) {
      setLoading(true);

      const payload = (model.planSchedule as IStudentPaymentPlanSchedule[]).map(r => ({
        ...r,
        expectedDate: toDateOnly(r.expectedDate),
        receivedDate: toDateOnly(r.receivedDate),
      }));

      console.log("model-For-Update", payload);
      PaymentPlanApi.UpdatePaymentPlan(payload).then(
        (response: any) => {
          if (response.resultStatus === 0) {
            setSnackBarProps((props: any) => {
              return {
                variant: "success",
                showSnackBar: true,
                messageInfo: "Payment Plan info updated successfully.",
              };
            });
            if (model.paymentPlanId) {
              getPaymentPlanByPaymentPlanId(model.paymentPlanId);
            }

          } else {
            setSnackBarProps((props: any) => {
              return {
                variant: "error",
                showSnackBar: true,
                messageInfo: "Payment Plan info not saved",
              };
            });
          }
          setLoading(false);
          refreshPaymentPlans();
        },
        (exception: any) => {
          setLoading(false);
          setSnackBarProps((props: any) => {
            return {
              variant: "error",
              showSnackBar: true,
              messageInfo: exception.message || "An error occurred while updating the payment plan.",
            };
          });
        }
      );
    }
  };


  const handleDelete = async () => {
    if (!selectedPaymentPlan?.paymentPlanId) return;
    setDeleteLoading(true);
    try {
      await PaymentPlanApi.deleteStudentPaymentPlan(selectedPaymentPlan.paymentPlanId);
      setSnackBarProps({
        variant: "success",
        showSnackBar: true,
        messageInfo: "Payment Plan deleted successfully.",
      });
      refreshPaymentPlans();
    } catch (error) {
      setSnackBarProps({
        variant: "error",
        showSnackBar: true,
        messageInfo: "An error occurred while deleting the payment plan.",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleExportToPdf = async () => {
    if (!model.paymentPlanId) return;
    try {
      const response = await PaymentPlanApi.getPaymentPlanReport(model.paymentPlanId, userSelectedCampus);
      const fileName = "PaymentPlan.pdf";
      const blob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      link.remove();
    } catch (error) {
      setSnackBarProps({
        variant: "error",
        showSnackBar: true,
        messageInfo: "Failed to export payment plan.",
      });
    }
  };

  const handleOpenDialog = () => {
    setModel(initialModel);
    setSelectedPaymentPlan(null);
    setOpenDialog(true);
  };

  const handleCloseOutPlan = () => {
    if (selectedPaymentPlan?.paymentPlanId == EmptyGuid) return;
    setConfirmCloseOutOpen(true);
  };

  const handleConfirmCloseOut = () => {
    setConfirmCloseOutOpen(false);
    PaymentPlanApi.closeOutPaymentPlan(selectedPaymentPlan.paymentPlanId).then((res: any) => {
      if (res && res.data && res.data.length > 0) {
        const firstPlan = res.data[0];
      } else {
      }
      if (model.paymentPlanId) {
        getPaymentPlanByPaymentPlanId(model.paymentPlanId);
      }
    });
  };

  const handleCancelCloseOut = () => {
    setConfirmCloseOutOpen(false);
  };

  const handleDialogClose = (newPlanId?: string) => {
    setOpenDialog(false);
    if (newPlanId) {
      getPaymentPlanByPaymentPlanId(newPlanId);
    } else if (selectedEnrollment?.stuEnrollId) {
      PaymentPlanApi.getStudentPaymentPlansByEnrollmentId(selectedEnrollment.stuEnrollId).then((res: any) => {
        if (res && res.data && res.data.length > 0) {
          const firstPlan = res.data[0];
          setPaymentPlanCount(res.data.length);
          getPaymentPlanByPaymentPlanId(firstPlan.value);
        } else {
          setSelectedPaymentPlan(null);
          setModel(initialModel);
        }
      });
    }
  };

  const isClosedOut =
    model.planSchedule &&
    model.planSchedule.length > 0 &&
    model.planSchedule.every((r: IStudentPaymentPlanSchedule) => Number(r.balance) === 0);

  return (
    <Grid className={classes.root}>
      <FormContext {...data}>
        <form>
          <Card className={classes.card}>
            <CustomCardTitle title="Payment Plan"></CustomCardTitle>
            <Grid className={classes.paymentPlanSection}>
              <Grid item md={8} sm={8} xs={12}>
                <Grid container alignItems="center" spacing={1}>

                  <Grid item xs={12} sm={4}>
                    <Typography>Number of Payment Plans</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Grid style={{ width: "95%" }}>
                      <Typography>
                        {paymentPlanCount}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} />

                  <Grid item xs={12} sm={4}>
                    <Typography>Payment Plan Description</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <PaymentPlanAutoComplete
                      stuEnrollId={selectedEnrollment?.stuEnrollId}
                      valueFilter={
                        model?.paymentPlanId
                          ? {
                            key: "value",
                            values: [model?.paymentPlanId],
                          }
                          : undefined
                      }
                      filterHandle={(v: any) => {
                        handleFieldOnChange(
                          "paymentPlanID",
                          v ? v.value : undefined
                        );
                        if (v && v.value) {
                          getPaymentPlanByPaymentPlanId(v.value);
                        }
                      }}

                      name="paymentPlanID"
                      id="paymentPlanID"
                    />
                  </Grid>

                  <Grid item xs={12} />

                  <Grid item xs={12} sm={4}>
                    <Typography>Total Due</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Grid style={{ width: "95%" }}>
                      <Typography>
                        {model.totalDue && model.totalDue > 0
                          ? model.totalDue.toLocaleString("en-US", { style: "currency", currency: "USD" })
                          : "$0.00"}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} />

                  <Grid item xs={12} sm={4}>
                    <Typography>Payment Plan Balance</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Grid style={{ width: "95%" }}>
                      <Typography>
                        {model.paymentPlanBalance && model.paymentPlanBalance > 0
                          ? model.paymentPlanBalance.toLocaleString("en-US", { style: "currency", currency: "USD" })
                          : "$0.00"}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} />

                  <Grid item md={6} sm={6} xs={8}>
                    <Button
                      size='medium'
                      color="secondary"
                      variant="contained"
                      type="button"
                      style={{ marginRight: "10px" }}
                      onClick={handleOpenDialog}
                    >
                      Create New Plan
                    </Button>

                    <ProgressSaveButton
                      text="Close Out Plan"
                      onClick={handleCloseOutPlan}
                      loading={confirmCloseOutOpen}
                      buttonClassName={classes.closeOutButton}
                      disabled={
                        !model.paymentPlanBalance ||
                        Number(model.paymentPlanBalance) === 0
                      }
                    ></ProgressSaveButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>

          {selectedPaymentPlan && selectedPaymentPlan.planSchedule && selectedPaymentPlan.planSchedule.length > 0 && (
            <Card className={classes.card} style={{ marginTop: 24 }}>
              <StudentPaymentPlanTable
                planSchedule={model.planSchedule}
                paymentPlanId={model.paymentPlanId}
                onUpdate={(newSchedule) => {
                  setModel((prev: any) => ({
                    ...prev,
                    planSchedule: newSchedule,
                  }));
                }}
                onDelete={(newSchedule) => {
                  setModel((prev: any) => ({
                    ...prev,
                    planSchedule: newSchedule,
                  }));
                }}
                onExportPDF={() => {
                  handleExportToPdf();
                }}
              />
            </Card>
          )}
        </form>
      </FormContext>
      <CustomSnackbar
        variant={snackBarProps.variant}
        message={snackBarProps.messageInfo}
        open={snackBarProps.showSnackBar}
        onClose={(event?: React.SyntheticEvent, reason?: string) => {
          setSnackBarProps((props: any) => {
            return { ...props, showSnackBar: false };
          });
        }}
      ></CustomSnackbar>

      {isClosedOut || (selectedPaymentPlan && selectedPaymentPlan.planSchedule) && (
        <Grid container spacing={2} style={{ marginTop: 16, marginLeft: 16, marginBottom: 16 }}>
          {selectedPaymentPlan.planSchedule.length > 0 && (
            <Grid item>
              <ProgressSaveButton
                text="update"
                buttonClassName={classes.updateButton}
                onClick={handleSave}
                disabled={
                  loading ||
                  !model.paymentPlanBalance ||
                  Number(model.paymentPlanBalance) === 0
                }
                loading={loading}
              ></ProgressSaveButton>
            </Grid>
          )}

          <Grid item>
            <ProgressSaveButton
              text="delete"
              buttonClassName={classes.deleteButton}
              onClick={handleDelete}
              loading={deleteLoading}
              disabled={
                deleteLoading ||
                (selectedPaymentPlan.planSchedule.some(
                  (row: any) => Number(row.receivedAmount) > 0
                ))
              }
            ></ProgressSaveButton>
          </Grid>
        </Grid>
      )}

      <Dialog open={openDialog} onClose={() => handleDialogClose()} fullWidth maxWidth="md" disableBackdropClick>
        <DialogContent>
          <CreatePaymentPlan
            onClose={handleDialogClose}
            selectedEnrollment={selectedEnrollment}
            paymentPlan={selectedPaymentPlan}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmCloseOutOpen}
        onClose={handleCancelCloseOut}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography>
            Unpaid installments will be removed and the Total Amount Due will be adjusted to the Payment Plan Balance. Do you wish to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCloseOut} color="default" variant="contained">
            Cancel
          </Button>
          <Button onClick={handleConfirmCloseOut} color="secondary" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default PaymentPlans;